import React, { useState } from 'react';
import {
  MdArrowBack, MdChevronRight, MdExpandMore, MdExpandLess,
  MdWarningAmber, MdCheckCircleOutline, MdInfoOutline, MdTrendingUp,
} from 'react-icons/md';
import { ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import { useProfile } from '../contexts/ProfileContext';

// ── Mock data ─────────────────────────────────────────────────────────────────

const ACCOUNTS = [
  {
    id: 'rrsp',
    label: 'RRSP',
    fullLabel: 'Registered Retirement Savings Plan',
    color: '#6A55F7',
    value: 68_450.24,
    gainPct: +12.4,
    ytdContrib: 6_000,
    annualRoom: 14_200,
    totalRoom: 14_200,
    usedRoom: 20_400,
    deadline: 'Mar 3, 2025',
    deadlineLabel: 'Tax deduction deadline',
    deadlineUrgent: false,
    taxBenefit: 'Reduces taxable income',
    taxBenefitDetail: 'Contributions are deducted from your taxable income. Investments grow tax-deferred until withdrawal.',
    maxAnnual: null,
    lifetimeMax: null,
    holdings: [
      { ticker: 'VFV.TO',  name: 'Vanguard S&P 500 ETF', pct: 60 },
      { ticker: 'XEQT.TO', name: 'iShares Core Equity ETF', pct: 40 },
    ],
    facts: [
      'Contribution room = 18% of prior year income (max $31,560 for 2025)',
      'Unused room carries forward indefinitely',
      'Withdrawals are taxed as income — ideal for lower-income retirement years',
      'Must convert to RRIF by age 71',
    ],
  },
  {
    id: 'tfsa',
    label: 'TFSA',
    fullLabel: 'Tax-Free Savings Account',
    color: '#7ec8a4',
    value: 42_180.00,
    gainPct: +8.7,
    ytdContrib: 3_500,
    annualRoom: 7_000,
    totalRoom: 7_000,
    usedRoom: 88_000,
    deadline: 'Jan 1, 2026',
    deadlineLabel: 'New $7,000 room added',
    deadlineUrgent: false,
    taxBenefit: 'Tax-free growth & withdrawals',
    taxBenefitDetail: 'Contributions are not tax-deductible, but all growth and withdrawals are completely tax-free.',
    maxAnnual: 7_000,
    lifetimeMax: 95_000,
    holdings: [
      { ticker: 'TD.TO',   name: 'TD Bank', pct: 54 },
      { ticker: 'BNS.TO',  name: 'Bank of Nova Scotia', pct: 46 },
    ],
    facts: [
      '$7,000 annual room in 2025 — $95,000 lifetime if eligible since 2009',
      'Withdrawals add back to room the following January',
      'No income tax on dividends, interest, or capital gains',
      'No impact on income-tested benefits (OAS, GIS, etc.)',
    ],
  },
  {
    id: 'fhsa',
    label: 'FHSA',
    fullLabel: 'First Home Savings Account',
    color: '#7aaee8',
    value: 8_000.00,
    gainPct: +2.1,
    ytdContrib: 8_000,
    annualRoom: 0,
    totalRoom: 8_000,
    usedRoom: 8_000,
    deadline: 'Dec 31, 2025',
    deadlineLabel: 'Annual $8,000 room resets',
    deadlineUrgent: false,
    taxBenefit: 'Deductible + tax-free withdrawal',
    taxBenefitDetail: 'Contributions are tax-deductible like an RRSP. Qualifying withdrawals to buy a first home are completely tax-free like a TFSA.',
    maxAnnual: 8_000,
    lifetimeMax: 40_000,
    holdings: [
      { ticker: 'ZAG.TO', name: 'BMO Aggregate Bond ETF', pct: 100 },
    ],
    facts: [
      '$8,000/year, $40,000 lifetime maximum',
      'Must be a first-time homebuyer to open or withdraw',
      'Unused annual room ($8k) carries forward once',
      'Can be transferred to RRSP/RRIF if home not purchased',
    ],
  },
  {
    id: 'nonreg',
    label: 'Non-Reg',
    fullLabel: 'Non-Registered Investment Account',
    color: '#e8c547',
    value: 15_320.00,
    gainPct: +5.3,
    ytdContrib: null,
    annualRoom: null,
    totalRoom: null,
    usedRoom: null,
    deadline: null,
    deadlineLabel: null,
    deadlineUrgent: false,
    taxBenefit: 'No limits, flexible',
    taxBenefitDetail: 'No contribution limits. Capital gains are 50% taxable (inclusion rate). Dividends from Canadian corps receive preferential tax treatment.',
    maxAnnual: null,
    lifetimeMax: null,
    holdings: [
      { ticker: 'MSFT',   name: 'Microsoft Corp.', pct: 73 },
      { ticker: 'ZAG.TO', name: 'BMO Agg Bond ETF', pct: 27 },
    ],
    facts: [
      'No contribution limits — invest any amount',
      '50% capital gains inclusion rate (as of 2024)',
      'Canadian eligible dividends get a dividend tax credit',
      'T3/T5 slips issued for taxable income each year',
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt  = v => '$' + Math.abs(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = v => '$' + (v / 1000).toFixed(0) + 'k';

// Room usage bar
const RoomBar = ({ used, total, color, ytd, annualRoom }) => {
  if (total == null) return null;
  const pctUsed = Math.min((annualRoom > 0 ? (total - annualRoom) / total : 1) * 100, 100);
  const pctYtd  = total > 0 ? Math.min((ytd / total) * 100, 100) : 0;
  const remaining = annualRoom ?? 0;

  return (
    <div className="ra-room-wrap">
      <div className="ra-room-labels">
        <span style={{ color: 'var(--text-medium)', fontSize: 11 }}>
          Contribution room
        </span>
        <span style={{ color, fontWeight: 700, fontSize: 13 }}>
          {fmt(remaining)} left
        </span>
      </div>
      <div className="ra-room-track">
        <div className="ra-room-fill-used"  style={{ width: `${pctUsed}%`, background: 'var(--bg-light-gray)' }} />
        <div className="ra-room-fill-ytd"   style={{ width: `${pctYtd}%`,  background: color, opacity: 0.85 }} />
        <div className="ra-room-fill-avail" style={{ flex: 1, background: `${color}28` }} />
      </div>
      <div className="ra-room-legend">
        <span><span className="ra-legend-dot" style={{ background: 'var(--bg-light-gray)', border: '1px solid var(--border-color)' }} />Prior years used</span>
        <span><span className="ra-legend-dot" style={{ background: color }} />YTD {fmt(ytd ?? 0)}</span>
        <span><span className="ra-legend-dot" style={{ background: `${color}50` }} />Available</span>
      </div>
    </div>
  );
};

// ── Account card (collapsible) ────────────────────────────────────────────────

const AccountCard = ({ acct, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const up = acct.gainPct >= 0;

  return (
    <div className="ra-card" style={{ borderTopColor: acct.color }}>
      {/* Card header — always visible */}
      <button className="ra-card-header" onClick={() => setOpen(o => !o)}>
        <div className="ra-card-left">
          <div className="ra-card-label" style={{ color: acct.color }}>{acct.label}</div>
          <div className="ra-card-full-label">{acct.fullLabel}</div>
        </div>
        <div className="ra-card-right">
          <div className="ra-card-value">{fmt(acct.value)}</div>
          <div className={`ra-card-gain ${up ? 'gain-up' : 'gain-down'}`}>
            <MdTrendingUp size={12} style={{ transform: up ? 'none' : 'scaleY(-1)' }} />
            {up ? '+' : ''}{acct.gainPct}%
          </div>
          {open ? <MdExpandLess size={20} style={{ color: 'var(--text-light)', marginLeft: 4 }} />
                : <MdExpandMore size={20} style={{ color: 'var(--text-light)', marginLeft: 4 }} />}
        </div>
      </button>

      {/* Tax benefit badge (always visible) */}
      <div className="ra-tax-badge" style={{ background: `${acct.color}1a`, color: acct.color }}>
        <MdCheckCircleOutline size={13} />
        {acct.taxBenefit}
      </div>

      {/* Expanded detail */}
      {open && (
        <div className="ra-card-body">

          {/* Contribution room bar */}
          {acct.annualRoom !== null && (
            <RoomBar
              used={acct.usedRoom}
              total={acct.usedRoom + acct.annualRoom}
              color={acct.color}
              ytd={acct.ytdContrib}
              annualRoom={acct.annualRoom}
            />
          )}

          {/* Annual / lifetime limits */}
          {(acct.maxAnnual || acct.lifetimeMax) && (
            <div className="ra-limits-row">
              {acct.maxAnnual && (
                <div className="ra-limit-chip">
                  <span className="ra-limit-lbl">Annual max</span>
                  <span className="ra-limit-val">{fmtK(acct.maxAnnual)}</span>
                </div>
              )}
              {acct.lifetimeMax && (
                <div className="ra-limit-chip">
                  <span className="ra-limit-lbl">Lifetime max</span>
                  <span className="ra-limit-val">{fmtK(acct.lifetimeMax)}</span>
                </div>
              )}
            </div>
          )}

          {/* Deadline alert */}
          {acct.deadline && (
            <div className={`ra-deadline${acct.deadlineUrgent ? ' urgent' : ''}`}>
              {acct.deadlineUrgent
                ? <MdWarningAmber size={14} style={{ flexShrink: 0 }} />
                : <MdInfoOutline size={14} style={{ flexShrink: 0 }} />}
              <span><strong>{acct.deadline}</strong> — {acct.deadlineLabel}</span>
            </div>
          )}

          {/* Tax benefit detail */}
          <div className="ra-tax-detail">
            <MdInfoOutline size={13} style={{ flexShrink: 0, marginTop: 1, color: acct.color }} />
            <span>{acct.taxBenefitDetail}</span>
          </div>

          {/* Holdings */}
          <div className="ra-holdings-title">Holdings in this account</div>
          {acct.holdings.map(h => (
            <div key={h.ticker} className="ra-holding-row">
              <span className="ra-holding-ticker">{h.ticker}</span>
              <span className="ra-holding-name">{h.name}</span>
              <div className="ra-holding-bar-wrap">
                <div className="ra-holding-bar" style={{ width: `${h.pct}%`, background: acct.color }} />
              </div>
              <span className="ra-holding-pct">{h.pct}%</span>
            </div>
          ))}

          {/* Key facts */}
          <div className="ra-facts-title">Key rules</div>
          {acct.facts.map((f, i) => (
            <div key={i} className="ra-fact-row">
              <span className="ra-fact-dot" style={{ background: acct.color }} />
              <span className="ra-fact-text">{f}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main screen ───────────────────────────────────────────────────────────────

const RegisteredAccountsScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();

  const totalRegistered = ACCOUNTS
    .filter(a => a.id !== 'nonreg')
    .reduce((s, a) => s + a.value, 0);

  const totalRoomLeft = ACCOUNTS
    .filter(a => a.annualRoom !== null)
    .reduce((s, a) => s + a.annualRoom, 0);

  return (
    <div className="screen ra-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('portfolio'))} aria-label="Back to portfolio">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Registered Accounts</h1>
        </div>
      </header>

      <div className="scroll-content gray-bg">

        {/* Summary banner */}
        <div className="ra-summary-banner">
          <div className="ra-summary-item">
            <div className="ra-summary-val">{fmt(totalRegistered)}</div>
            <div className="ra-summary-lbl">Registered assets</div>
          </div>
          <div className="ra-summary-divider" />
          <div className="ra-summary-item">
            <div className="ra-summary-val" style={{ color: '#7ec8a4' }}>{fmt(totalRoomLeft)}</div>
            <div className="ra-summary-lbl">Total room available</div>
          </div>
          {loadedProfileData && (
            <>
              <div className="ra-summary-divider" />
              <div className="ra-summary-item">
                <div className="ra-summary-val" style={{ fontSize: 12 }}>{loadedProfileData.name.split(' ')[0]}</div>
                <div className="ra-summary-lbl">Active profile</div>
              </div>
            </>
          )}
        </div>

        {/* Tax advantage callout */}
        <div className="ra-tax-callout">
          <MdCheckCircleOutline size={16} style={{ color: '#7ec8a4', flexShrink: 0 }} />
          <span>
            Maximizing your registered accounts is one of the most impactful tax strategies available to Canadians.
            You have <strong style={{ color: '#7ec8a4' }}>{fmt(totalRoomLeft)}</strong> in unused contribution room.
          </span>
        </div>

        {/* Account cards */}
        {ACCOUNTS.map((acct, i) => (
          <AccountCard key={acct.id} acct={acct} defaultOpen={i === 0} />
        ))}

        {/* CTA */}
        <div className="ra-cta-row">
          <button className="ra-cta-btn" onClick={() => onNavigate('portfolio')}>
            <MdArrowBack size={16} />
            Back to Portfolio
          </button>
        </div>

        <div className="viz-footer" style={{ margin: '0 16px 28px' }}>
          <span>TD Wealth · Registered Accounts · IVA Demo · For illustration only</span>
        </div>
      </div>
    </div>
  );
};

export default RegisteredAccountsScreen;
